import { useSyncExternalStore } from 'react';
import type { Vec2 } from './types';
import { DEFAULT_COMPANION_ID, getCompanion } from './registry';

const STORAGE_KEY = 'nula_companion';

/** The size the companion shipped at, and what every sprite was drawn for. */
export const DEFAULT_COMPANION_SCALE = 0.4;
/** Small enough to be unobtrusive without losing the character's face. */
export const MIN_COMPANION_SCALE = 0.2;
/** Past this the sprite's own pixels are visible; the atlas has no more to give. */
export const MAX_COMPANION_SCALE = 0.8;

export interface CompanionPrefs {
  enabled: boolean;
  companionId: string;
  position: Vec2;
  scale: number;
}

const DEFAULTS: CompanionPrefs = {
  enabled: false,
  companionId: DEFAULT_COMPANION_ID,
  position: { x: 0.85, y: 0.9 },
  scale: DEFAULT_COMPANION_SCALE,
};

let current: CompanionPrefs = DEFAULTS;
let initialized = false;
const listeners = new Set<() => void>();

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

const clampScale = (n: number): number =>
  Math.min(MAX_COMPANION_SCALE, Math.max(MIN_COMPANION_SCALE, n));

function emit() {
  for (const l of listeners) l();
}

function parse(payload: string | null): CompanionPrefs {
  if (!payload) return DEFAULTS;

  let raw: unknown;
  try {
    raw = JSON.parse(payload);
  } catch {
    return DEFAULTS;
  }

  if (typeof raw !== 'object' || raw === null) return DEFAULTS;
  const obj = raw as Partial<CompanionPrefs>;

  const companionId = typeof obj.companionId === 'string' && getCompanion(obj.companionId)
    ? obj.companionId
    : DEFAULTS.companionId;

  const pos = obj.position;
  const position = pos && typeof pos.x === 'number' && typeof pos.y === 'number'
    ? { x: clamp01(pos.x), y: clamp01(pos.y) }
    : DEFAULTS.position;

  return {
    enabled: typeof obj.enabled === 'boolean' ? obj.enabled : DEFAULTS.enabled,
    companionId,
    position,
    scale: typeof obj.scale === 'number' && Number.isFinite(obj.scale)
      ? clampScale(obj.scale)
      : DEFAULTS.scale,
  };
}

function readStored(): CompanionPrefs {
  try {
    return parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULTS;
  }
}

function persist(prefs: CompanionPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Private mode or a webview without storage — the choice just won't stick.
  }
}

function initStore() {
  if (initialized) return;
  initialized = true;
  current = readStored();
}

function update(patch: Partial<CompanionPrefs>) {
  initStore();
  current = { ...current, ...patch };
  persist(current);
  emit();
}

export function setCompanionEnabled(enabled: boolean) {
  update({ enabled });
}

export function setCompanionId(companionId: string) {
  // parse() rejects an unbundled id on read; the setter honours the same
  // invariant so a bad write cannot sit in storage until the next reload.
  if (!getCompanion(companionId)) return;
  update({ companionId });
}

export function setCompanionScale(scale: number) {
  update({ scale: clampScale(scale) });
}

export function setCompanionPosition(position: Vec2) {
  update({ position: { x: clamp01(position.x), y: clamp01(position.y) } });
}

export function readCompanionPrefs(): CompanionPrefs {
  initStore();
  return current;
}

/** Test-only: the module store outlives a render, so isolation needs a reset. */
export function resetCompanionPrefsForTests() {
  current = DEFAULTS;
  initialized = false;
  listeners.clear();
}

function subscribe(listener: () => void) {
  initStore();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useCompanionPrefs() {
  const prefs = useSyncExternalStore(subscribe, readCompanionPrefs, () => DEFAULTS);
  return {
    ...prefs,
    setEnabled: setCompanionEnabled,
    setCompanionId,
    setPosition: setCompanionPosition,
    setScale: setCompanionScale,
  };
}
